// UserModals.tsx
import { useState } from "react";
import { Modal, Form, Input, Upload, Space, Button, Checkbox, message, Tooltip } from "antd";
import { UploadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { API_BASE_URL } from "../../constants/config";
import { useTranslation } from "react-i18next";
import { usePanelsStore } from "../../shared/stores/usePanelsStore";
import PanelSelector from "../../shared/components/PanelSelector";
import { getAndSanitize } from "../../shared/utils/sanitization";

interface UserRecord {
  id: string;
  username: string;
  currentBalance: number;
  userId?: string;
  accountName?: string;
  password?: string;
  createdAt: string;
  historyData?: any[];
}

interface ActionModalState {
  type: ActionType | null;
  userData?: UserRecord;
}

interface UserModalsProps {
  createModalOpen?: boolean;
  setCreateModalOpen?: (value: boolean) => void;
  actionModalOpen: ActionModalState;
  setActionModalOpen: (value: ActionModalState) => void;
  currentPanelId: string;
}

// Cambiar el tipo para permitir "balance"
type ActionType = "deposit" | "withdraw" | "balance";

export const UserModals: React.FC<UserModalsProps> = ({
  createModalOpen = false,
  setCreateModalOpen = () => {},
  actionModalOpen,
  setActionModalOpen,
  currentPanelId,
}) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<any[]>([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [skipPanel, setSkipPanel] = useState(false);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  
  // Panel selector states
  const [panelSelectorOpen, setPanelSelectorOpen] = useState(false);
  const [isLoadingPanels, setIsLoadingPanels] = useState(false);
  const [availablePanels, setAvailablePanels] = useState<Array<{ id: string; name?: string; isActive?: boolean }>>([]);
  const [pendingAction, setPendingAction] = useState<{
    type: ActionType | null;
    formValues: any | null;
  }>({ type: null, formValues: null });

  const handleCreateUser = async (values: any) => {
    try {
      await axios.post(
        `${API_BASE_URL}/api/panel/operations/users`,
        {
          ...values,
          panelId: currentPanelId,
        },
        {
          headers: { Authorization: `Bearer ${getAndSanitize("token")}` },
        }
      );

      setCreateModalOpen(false);
      form.resetFields();
      await queryClient.invalidateQueries({ queryKey: ["panel-users"] });
      message.success("User created successfully");
    } catch (error) {
      message.error("Error creating user");
    }
  };

  const handleAction = async (values: any) => {
    if (!actionModalOpen.type || !actionModalOpen.userData) return;

    // If skipPanel is selected, use direct operation
    if (skipPanel) {
      executeDirectAction(values);
      return;
    }

    // If currentPanelId is already provided, use it directly
    if (currentPanelId) {
      executeAction(values, currentPanelId);
      return;
    }

    // Otherwise, fetch available panels
    setIsLoadingPanels(true);
    try {
      const panels = await usePanelsStore.getState().fetchPanels();
      setAvailablePanels(panels);
      setIsLoadingPanels(false);

      if (!panels || panels.length === 0) {
        message.error("No hay paneles disponibles para realizar esta operación");
        return;
      }

      // If there's only one panel, use it directly
      if (panels.length === 1) {
        executeAction(values, panels[0].id);
        return;
      }

      // If multiple panels, save the pending action and show panel selector
      setPendingAction({
        type: actionModalOpen.type,
        formValues: values
      });
      setPanelSelectorOpen(true);
    } catch (error) {
      message.error(t("users.userModals.operationFailed"));
      console.error('Error fetching panels:', error);
      setIsLoadingPanels(false);
    }
  };

  const executeDirectAction = async (values: any) => {
    setSubmitLoading(true);
    try {
      const formData = new FormData();
      formData.append("username", actionModalOpen.userData!.username);
      formData.append("amount", values.amount);
      formData.append("bonus", values.bonus || "0");
      formData.append("type", actionModalOpen.type || "");
      
      // Get the admin ID from the same place you get the token
      const adminId = getAndSanitize("userId");
      formData.append("adminId", adminId);

      if (values.receipt?.[0]?.originFileObj) {
        formData.append("receipt", values.receipt[0].originFileObj);
      }
      
      const response = await axios.post(
        `${API_BASE_URL}/api/operations/direct`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${getAndSanitize("token")}`,
            // Add this to ensure the adminId is also in the headers
            "x-admin-id": adminId
          },
        }
      );
      
      if (response.data.success) {
        message.success(
          actionModalOpen.type === "deposit"
            ? t("users.userModals.operationSuccess.deposit")
            : t("users.userModals.operationSuccess.withdraw")
        );
        
        setActionModalOpen({ type: null });
        form.resetFields();
        
        // Refresh data
        setTimeout(async () => {
          try {
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: ["panel-users"] }),
              queryClient.invalidateQueries({ queryKey: ["operations"] })
            ]);
          } catch (error) {
            console.error("Error refreshing data:", error);
          }
        }, 100);
      } else {
        message.error(response.data.message || t("users.userModals.operationFailed"));
      }
    } catch (error: any) {
      console.error("Direct operation error:", error);
      message.error(error.response?.data?.message || t("users.userModals.operationFailed"));
    } finally {
      setSubmitLoading(false);
    }
  };

  const executeAction = async (values: any, panelId: string) => {
    setSubmitLoading(true);
    try {
      const formData = new FormData();
      formData.append("username", actionModalOpen.userData!.username);
      formData.append("amount", values.amount);
      formData.append("bonus", values.bonus || "0");
      formData.append("panelId", panelId);
      
      // Get the admin ID from the same place you get the token
      const adminId = getAndSanitize("userId");
      formData.append("adminId", adminId);

      if (values.receipt?.[0]?.originFileObj) {
        formData.append("receipt", values.receipt[0].originFileObj);
      }
      
      // Using the correct endpoint path with /operations/
      const response = await axios.post(
        `${API_BASE_URL}/api/panel/operations/${actionModalOpen.type}`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${getAndSanitize("token")}`,
            // Add this to ensure the adminId is also in the headers
            "x-admin-id": adminId
          },
        }
      );
      
      if (response.data.success) {
        setActionModalOpen({ type: null });
        form.resetFields();
        
        // Add a small delay before invalidating queries to ensure UI updates first
        setTimeout(async () => {
          try {
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: ["panel-users"] }),
              queryClient.invalidateQueries({ queryKey: ["operations"] })
            ]);
          } catch (error) {
            console.error("Error refreshing data:", error);
            // Don't show error to user since the operation itself succeeded
          }
        }, 100);
        
        // Show success message
        message.success(
          actionModalOpen.type === "deposit"
            ? t("users.userModals.operationSuccess.deposit")
            : t("users.userModals.operationSuccess.withdraw")
        );
      } else {
        message.error(response.data.message || t("users.userModals.operationFailed"));
      }
    } catch (error) {
      console.error("Operation error:", error);
      message.error(t("users.userModals.operationFailed"));
    } finally {
      // Ensure submit loading is always turned off, even if there's an error
      setSubmitLoading(false);
    }
  };

  const handlePanelSelect = (panelId: string) => {
    // Close the panel selector
    setPanelSelectorOpen(false);

    // Check if there's a pending action
    if (!pendingAction.type || !pendingAction.formValues) return;

    // Execute the action with the selected panel
    executeAction(pendingAction.formValues, panelId);

    // Reset pending action
    setPendingAction({ type: null, formValues: null });
  };

  return (
    <>
      <Modal
        title={t("users.userModals.createUser")}
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleCreateUser}>
          <Form.Item label={t("users.userModals.username")} name="username" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t("users.userModals.userId")} name="user_id">
            <Input />
          </Form.Item>
          <Form.Item label={t("users.userModals.accountName")} name="account_name">
            <Input />
          </Form.Item>
          <Form.Item label={t("users.userModals.phone")} name="phone">
            <Input />
          </Form.Item>
          <Form.Item style={{ textAlign: "right" }}>
            <Space>
              <Button onClick={() => setCreateModalOpen(false)}>{t("users.userModals.cancel")}</Button>
              <Button type="primary" htmlType="submit">
                {t("users.userModals.create")}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          actionModalOpen.type === "deposit"
            ? t("users.userModals.deposit.title")
            : actionModalOpen.type === "withdraw"
            ? t("users.userModals.withdraw.title")
            : ""
        }
        open={!!actionModalOpen.type}
        onCancel={() => setActionModalOpen({ type: null })}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleAction}>
          {(actionModalOpen.type === "deposit" || actionModalOpen.type === "withdraw") && (
            <>
              <Form.Item label={t("users.userModals.deposit.amount")} name="amount" rules={[{ required: true }]}>
                <Input type="number" />
              </Form.Item>
              <Form.Item label={t("users.userModals.deposit.bonus")} name="bonus" initialValue={0}>
                <Input type="number" />
              </Form.Item>
              <Form.Item
                label={t("users.userModals.deposit.receipt")}
                name="receipt"
                valuePropName="fileList"
                getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
              >
                <Upload
                  beforeUpload={() => false}
                  fileList={fileList}
                  onChange={({ fileList }) => setFileList(fileList)}
                >
                  <Button icon={<UploadOutlined />}>{t("users.userModals.deposit.uploadReceipt")}</Button>
                </Upload>
              </Form.Item>
              <Form.Item name="notifyUser" valuePropName="checked">
                <Checkbox>{t("users.userModals.deposit.notifyUser")}</Checkbox>
              </Form.Item>
              <Form.Item name="skipPanel" valuePropName="checked">
                <Checkbox onChange={(e) => setSkipPanel(e.target.checked)}>
                  {t("operations.skipPanel", "Direct operation (skip panel)")}
                </Checkbox>
                <Tooltip title={t("operations.skipPanelTooltip", "Register the operation directly in database without processing through panel")}>
                  <InfoCircleOutlined style={{ marginLeft: 8 }} />
                </Tooltip>
              </Form.Item>
            </>
          )}

          <Form.Item style={{ textAlign: "right" }}>
            <Space>
              <Button onClick={() => setActionModalOpen({ type: null })}>{t("users.userModals.cancel")}</Button>
              <Button type="primary" htmlType="submit" loading={submitLoading}>
                {t("users.userModals.submit")}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Panel Selector */}
      <PanelSelector
        isOpen={panelSelectorOpen}
        onClose={() => setPanelSelectorOpen(false)}
        onPanelSelect={handlePanelSelect}
        panels={availablePanels}
        loading={isLoadingPanels}
        actionTitle={
          pendingAction.type === "deposit"
            ? t("users.userModals.deposit.title")
            : pendingAction.type === "withdraw"
            ? t("users.userModals.withdraw.title")
            : t("users.userModals.selectPanel") || "Seleccionar Panel"
        }
        actionDescription={
          pendingAction.type === "deposit"
            ? t("users.userModals.deposit.selectPanel") || "Selecciona el panel para realizar el depósito"
            : pendingAction.type === "withdraw"
            ? t("users.userModals.withdraw.selectPanel") || "Selecciona el panel para realizar el retiro"
            : t("users.userModals.selectPanelDescription") || "Selecciona un panel para continuar"
        }
      />
    </>
  );
};